import { useState, useEffect, useCallback, useRef } from "react";

interface VerificationStatus {
  status: "pending" | "completed" | "error";
  result?: any;
  error?: string;
}

interface UseVerificationPollingOptions {
  nonce: string;
  interval?: number; // 폴링 간격 (ms)
  maxAttempts?: number; // 최대 시도 횟수
  enabled?: boolean; // 폴링 활성화 여부
}

interface UseVerificationPollingReturn {
  verificationStatus: VerificationStatus;
  presentationResult: any;
  presentationRequestResult: any;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
  resetStatus: () => void;
}

export const useVerificationPolling = ({
  nonce,
  interval = 2000, // 기본 2초
  maxAttempts = 150, // 기본 5분 (2초 * 150회)
  enabled = true,
}: UseVerificationPollingOptions): UseVerificationPollingReturn => {
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>({
      status: "pending",
    });

  const [presentationResult, setPresentationResult] = useState<any>();
  const [presentationRequestResult, setPresentationRequestResult] =
    useState<any>();
  const [isPolling, setIsPolling] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef(0);

  const checkVerificationStatus = useCallback(async () => {
    if (!nonce) return;

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_VERIFIER_SERVER_ENDPOINT
        }/api/v1/presentation_result/${nonce}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.data.status === "completed") {
          setVerificationStatus({
            status: "completed",
            result: result.data.result,
          });
          setPresentationResult(result.data.presentation);
          setPresentationRequestResult(result.data.presentationRequest);
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        } else {
          // 여전히 pending 상태
          attemptCountRef.current += 1;

          // 최대 시도 횟수 초과 시 폴링 중단
          if (attemptCountRef.current >= maxAttempts) {
            setVerificationStatus({
              status: "error",
              error: "검증 대기 시간이 초과되었습니다.",
            });
            setIsPolling(false);
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }
      }
    } catch (error) {
      console.error("검증 상태 확인 중 오류:", error);
      setVerificationStatus({
        status: "error",
        error: error instanceof Error ? error.message : "알 수 없는 오류",
      });
      setIsPolling(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [nonce, maxAttempts]);

  const startPolling = useCallback(() => {
    if (!enabled || !nonce || isPolling) return;

    setIsPolling(true);
    attemptCountRef.current = 0;
    setVerificationStatus({ status: "pending" });

    // 즉시 한 번 확인
    checkVerificationStatus();

    // 주기적으로 확인
    intervalRef.current = setInterval(checkVerificationStatus, interval);
  }, [enabled, nonce, isPolling, checkVerificationStatus, interval]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetStatus = useCallback(() => {
    setVerificationStatus({ status: "pending" });
    attemptCountRef.current = 0;
  }, []);

  // nonce가 변경되면 폴링 재시작
  useEffect(() => {
    if (enabled && nonce) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [nonce, enabled]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    verificationStatus,
    presentationResult,
    presentationRequestResult,
    isPolling,
    startPolling,
    stopPolling,
    resetStatus,
  };
};
